import { StorageState, StorageMetadata } from '../types/storage';
import { getStorageEstimate } from '../utils/getStorageEstimate';
import loggerService, { LoggerLevel } from '../../log';

/**
 * Singleton service responsible for maintaining current storage state
 * and exposing it for baseMetadata usage.
 *
 * Features:
 * - Periodic storage estimate updates (default 60 seconds)
 * - Handles Storage API availability gracefully
 * - Handles visibility change (resume detection)
 * - Never emits standalone analytics events
 * - Always returns synchronously
 * - Auto-initializes on first use
 */
class StorageMetadataService {
  private currentState: StorageState = { lastUpdated: 0 };
  private updateInterval?: NodeJS.Timeout;
  private visibilityChangeHandler?: () => void;
  private readonly updateIntervalMs: number = 60000; // 60 seconds
  private isInitialized = false;
  private initPromise?: Promise<void>;

  /**
   * Initialize the service and set up periodic updates.
   * Safe to call multiple times - will only initialize once.
   * Returns the same promise if initialization is already in progress.
   */
  async init(): Promise<void> {
    // If already initialized, return immediately
    if (this.isInitialized) {
      return;
    }

    // If initialization is in progress, return the existing promise
    if (this.initPromise) {
      return this.initPromise;
    }

    // Start initialization and cache the promise
    this.initPromise = (async () => {
      try {
        // Get initial state
        this.currentState = await getStorageEstimate();
        this.isInitialized = true;

        // Set up periodic updates
        this.setupPeriodicUpdates();

        // Set up visibility change handler
        this.setupEventListeners();
      } catch (error) {
        // Reset promise on error so we can retry
        this.initPromise = undefined;
        throw error;
      }
    })();

    return this.initPromise;
  }

  /**
   * Get the current storage metadata for baseMetadata injection.
   * Always returns synchronously - never async.
   * Auto-initializes the service on first call if not already initialized.
   *
   * Note: On the first call, this may return a default state if initialization hasn't completed yet.
   * Subsequent calls will return the cached state. To ensure data is available,
   * initialize the service early (e.g., at app startup).
   *
   * @returns StorageMetadata object (may have default values if not yet initialized)
   */
  getStorageMetadata(): StorageMetadata {
    // Auto-initialize on first use if not already initialized
    if (!this.isInitialized && !this.initPromise) {
      // Trigger async initialization but return current state (may have default values initially)
      this.init().catch((err) => {
        loggerService.log(LoggerLevel.WARN, 'Failed to initialize storage metadata service:', err);
      });
    }

    return this.currentState;
  }

  /**
   * Handle visibility change events (tab resume, app foreground).
   * Updates storage state when the page becomes visible.
   */
  async handleVisibilityChange(): Promise<void> {
    if (document.visibilityState === 'visible') {
      // Page became visible - update storage state
      await this.updateState();
    }
  }

  /**
   * Clean up all intervals and event listeners.
   * Should be called when the service is no longer needed.
   */
  cleanup(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = undefined;
    }

    this.removeEventListeners();
    this.isInitialized = false;
    this.initPromise = undefined;
    this.currentState = { lastUpdated: 0 };
  }

  /**
   * Update storage state immediately.
   */
  private async updateState(): Promise<void> {
    this.currentState = await getStorageEstimate();
  }

  /**
   * Set up periodic storage updates.
   */
  private setupPeriodicUpdates(): void {
    this.updateInterval = setInterval(async () => {
      await this.updateState();
    }, this.updateIntervalMs);
  }

  /**
   * Set up event listeners for storage state changes.
   */
  private setupEventListeners(): void {
    // Visibility change (resume detection)
    this.visibilityChangeHandler = () => {
      this.handleVisibilityChange().catch((err) => {
        loggerService.log(LoggerLevel.ERROR, 'Failed to handle visibility change:', err);
      });
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Remove all event listeners.
   */
  private removeEventListeners(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }
  }
}

// Export singleton instance
export const storageMetadataService = new StorageMetadataService();

// Eagerly initialize the service when module loads (if in browser environment)
// This ensures storage metadata is available on the first event tracking call
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  storageMetadataService.init().catch((err) => {
    loggerService.log(
      LoggerLevel.WARN,
      'Failed to eagerly initialize storage metadata service:',
      err
    );
  });
}
